import * as React from "react"
import { createId } from "@paralleldrive/cuid2"
import { FileIcon, Upload, X } from "lucide-react"

import { Button } from "@/components/shadcn/ui/button"
import { Label } from "@/components/shadcn/ui/label"
import type { FileField as FileFieldType } from "@/models/new/forms.types"
import type { ResponseValue } from "@/models/new/formResponse.types"
import type { PendingReportUpload } from "../report-creation-media"
import { isAcceptedFile } from "./file-accept"

interface Props {
  field: FileFieldType
  value?: ResponseValue
  onChange: (fieldId: string, value: ResponseValue) => void
  isCreating?: boolean
  pendingUploads?: PendingReportUpload[]
  onPendingUploadsChange?: (uploads: PendingReportUpload[]) => void
}

const MAX_FILE_SIZE = 10 * 1024 * 1024

const FileField = ({
  field,
  onChange,
  isCreating,
  pendingUploads = [],
  onPendingUploadsChange,
}: Props) => {
  const [error, setError] = React.useState<string>()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const inputId = React.useId()

  if (!isCreating) return null

  const handleFiles = (files: FileList | null) => {
    if (!files) return
    const selected = Array.from(files)
    const oversized = selected.find((file) => file.size > MAX_FILE_SIZE)
    if (oversized) {
      setError(`${oversized.name} is larger than 10MB.`)
      return
    }
    const unsupported = selected.find((file) => !isAcceptedFile(file, field.accept))
    if (unsupported) {
      setError(`${unsupported.name} is not an accepted file type.`)
      return
    }

    setError(undefined)
    const uploads = selected.map((file) => ({ uploadId: createId(), file }))
    const nextUploads = field.multiple ? [...pendingUploads, ...uploads] : uploads.slice(0, 1)
    onPendingUploadsChange?.(nextUploads)
    onChange(field.id, { fieldName: field.name, type: "file", value: [] })
    if (inputRef.current) inputRef.current.value = ""
  }

  return (
    <div className="w-1/2 p-4">
      <Label htmlFor={inputId} className="font-medium text-gray-700">
        {field.label}
        {field.required ? <span className="ml-1 text-red-600">*</span> : null}
      </Label>
      {field.description ? (
        <p className="mt-1 text-sm text-slate-500">{field.description}</p>
      ) : null}

      <input
        id={inputId}
        ref={inputRef}
        type="file"
        className="sr-only"
        accept={field.accept.join(",")}
        multiple={field.multiple}
        onChange={(event) => handleFiles(event.target.files)}
      />
      <Button
        type="button"
        variant="outline"
        className="mt-3"
        onClick={() => inputRef.current?.click()}
      >
        <Upload className="h-4 w-4" />
        {field.multiple ? "Choose files" : "Choose file"}
      </Button>

      {pendingUploads.length > 0 ? (
        <ul className="mt-3 space-y-2">
          {pendingUploads.map((upload) => (
            <li
              key={upload.uploadId}
              className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3"
            >
              <FileIcon className="h-4 w-4 shrink-0 text-slate-500" />
              <span className="min-w-0 flex-1 truncate text-sm text-slate-700">
                {upload.file.name}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove ${upload.file.name}`}
                onClick={() =>
                  onPendingUploadsChange?.(
                    pendingUploads.filter(({ uploadId }) => uploadId !== upload.uploadId),
                  )
                }
              >
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      {error ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}
    </div>
  )
}

export default FileField
